/**
 * @fileoverview Apple Business Input Table component for the Apple Linked Account Step.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/enterprise/errorcallout/errorcallout';
import {AppleCountryInfo} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/applecountryinfo';
import {LinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/linkedaccount';
import {useAppleCountryInfos} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/datasource/useapplecountryinfos';
import {Checkbox} from '/ui/components/checkbox/checkbox';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';
import {MenuController} from '/ui/components/menu/menucontroller';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

/**
 * @param {{
 *  publisherId: number,
 *  linkedAccount: LinkedAccount,
 *  appleCountryInfos?: Array<AppleCountryInfo>,
 *  setCountryInfos: function(Array<AppleCountryInfo>=): void,
 *  isDisabled: boolean,
 * }} props
 *
 * @returns {React.ReactNode}
 */
export function AppleBusinessInput({publisherId, linkedAccount, appleCountryInfos, setCountryInfos, isDisabled}) {
  const [includeAllCountries, setIncludeAllCountries] = React.useState(true);
  const appleCountryInfosState = useAppleCountryInfos(publisherId, linkedAccount.id);

  React.useEffect(() => {
    if (appleCountryInfos?.length === 0 && appleCountryInfosState.status === 'success' && appleCountryInfosState.data !== undefined) {
      setCountryInfos(appleCountryInfosState.data.appleCountryInfos);
    }
  }, [appleCountryInfosState.status, appleCountryInfosState.data, appleCountryInfos, setCountryInfos]);

  // If all country rows' excluded state is the same, update the column exclusion checkbox to match that state
  React.useEffect(() => {
    const shouldIncludeAllCountries = appleCountryInfos?.every(info => !info.excluded);
    if (shouldIncludeAllCountries) {
      setIncludeAllCountries(true);
    } else {
      setIncludeAllCountries(false);
    }
  }, [appleCountryInfos, setIncludeAllCountries]);


  let content;
  if (appleCountryInfosState.status === 'error') {
    content = <ErrorCallout onReload={appleCountryInfosState.refetch} />;
  } else if (appleCountryInfosState.status !== 'success' || appleCountryInfosState.data === undefined) {
    content = <ContentSkeleton type="box" height={100} />;
  } else if (appleCountryInfos !== undefined) {
    content = <Table
      columns={getColumns(includeAllCountries, setIncludeAllCountries, appleCountryInfos, setCountryInfos, isDisabled)}
      dataRows={getRows(appleCountryInfos, setCountryInfos, isDisabled)}
      label={yext.msg('Linked Account Table')}
    />;
  }

  return (
    <>
      {content}
    </>
  );
}

const COLUMN_IDS = {
  EXCLUDE_ALL_COUNTRIES: 'includeAllCountries',
  COUNTRY: 'country',
  APPLE_COMPANY_ID: 'appleCompanyId',
  APPLE_BUSINESS_ID: 'appleBusinessId',
};

/**
 * @param {boolean} includeAllCountries
 * @param {function(boolean): void} setIncludeAllCountries
 * @param {Array<AppleCountryInfo>} appleCountryInfos
 * @param {function(Array<AppleCountryInfo>): void} setCountryInfos
 * @param {boolean} isDisabled
 */
function getColumns(includeAllCountries, setIncludeAllCountries, appleCountryInfos, setCountryInfos, isDisabled) {
  const columns = [
    {
      id: COLUMN_IDS.EXCLUDE_ALL_COUNTRIES,
      label: <Checkbox
        checked={includeAllCountries}
        disabled={isDisabled}
        onChange={() => {
          setIncludeAllCountries(!includeAllCountries);
          setCountryInfos(appleCountryInfos.map(appleCountryInfo => ({
            ...appleCountryInfo,
            excluded: includeAllCountries,
          })));
        }}
      />,
      width: '15%',
    },
    {
      id: COLUMN_IDS.COUNTRY,
      label: yext.msg('Country'),
      width: '25%',
    },
    {
      id: COLUMN_IDS.APPLE_COMPANY_ID,
      label: <Tooltip
        unclearText={yext.msg('Apple Company ID')}
        tooltipText={yext.msg('The ID associated with your Apple Account. Numerical Values Only.')}
      />,
      width: '30%',
    },
    {
      id: COLUMN_IDS.APPLE_BUSINESS_ID,
      label: <Tooltip
        unclearText={yext.msg('Apple Business ID')}
        tooltipText={yext.msg('The ID associated with an individual Business Folder in your Apple Account.')}
      />,
      width: '30%',
    },
  ];

  return columns;
}

/**
 * @param {Array<AppleCountryInfo>} appleCountryInfos
 * @param {function(Array<AppleCountryInfo>): void} setCountryInfos
 * @param {boolean} isDisabled
 */
function getRows(appleCountryInfos, setCountryInfos, isDisabled) {
  return appleCountryInfos.map(appleCountryInfo => getRow(appleCountryInfo, appleCountryInfos, setCountryInfos, isDisabled));
}

/**
 * @param {AppleCountryInfo} appleCountryInfoRow
 * @param {Array<AppleCountryInfo>} appleCountryInfos
 * @param {function(Array<AppleCountryInfo>): void} setCountryInfos
 * @param {boolean} isDisabled
 */
function getRow(appleCountryInfoRow, appleCountryInfos, setCountryInfos, isDisabled) {
  return {
    id: appleCountryInfoRow.country,
    [COLUMN_IDS.EXCLUDE_ALL_COUNTRIES]: <Checkbox
      disabled={isDisabled}
      checked={!appleCountryInfoRow.excluded}
      onChange={() => {
        setCountryInfos(
          appleCountryInfos.map(business => {
            if (business.country === appleCountryInfoRow.country) {
              return {
                ...business,
                excluded: !business.excluded,
              };
            }
            return business;
          }),
        );
      }}
    />,
    [COLUMN_IDS.COUNTRY]: appleCountryInfoRow.country,
    [COLUMN_IDS.APPLE_COMPANY_ID]: appleCountryInfoRow.appleCompanyId,
    [COLUMN_IDS.APPLE_BUSINESS_ID]:
      <MenuController
        label={appleCountryInfoRow.selectedAppleBusiness === undefined
          ? yext.msg('Select Apple Business ID')
          : appleCountryInfoRow.selectedAppleBusiness.displayName
        }
        enableSearch={true}
        disabled={appleCountryInfoRow.excluded}
        allowContainerOverflow={true}
        actions={appleCountryInfoRow.appleBusinesses.map(appleBusiness => ({
          name: appleBusiness.displayName,
          label: appleBusiness.displayName,
          secondaryText: appleBusiness.appleBusinessId,
          callback: () => {
            setCountryInfos(appleCountryInfos.map(appleCountryInfo => {
              if (appleCountryInfo.country === appleCountryInfoRow.country) {
                return {
                  ...appleCountryInfo,
                  selectedAppleBusiness: appleBusiness,
                };
              }
              return appleCountryInfo;
            }));
          },
        }))}
      />,
  };
}
